import React, { useState, useEffect } from "react";
import { FiSave, FiX } from "react-icons/fi";

const ProjectForm = ({
  selectedProject,
  onSubmit,
  onCancel,
  isLoading = false,
}) => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [thumbnail, setThumbnail] = useState("");
  const [link, setLink] = useState("");
  const [published, setPublished] = useState(true);
  const [featured, setFeatured] = useState(false);
  const [challenge, setChallenge] = useState("");
  const [approach, setApproach] = useState("");
  const [outcome, setOutcome] = useState("");

  useEffect(() => {
    if (selectedProject) {
      setTitle(selectedProject.title || "");
      setDescription(selectedProject.description || "");
      setThumbnail(selectedProject.thumbnail || "");
      setLink(selectedProject.link || "");
      setPublished(selectedProject.published ?? true);
      setFeatured(selectedProject.featured ?? false);
      setChallenge(selectedProject.challenge || "");
      setApproach(selectedProject.approach || "");
      setOutcome(selectedProject.outcome || "");
    } else {
      setTitle("");
      setDescription("");
      setThumbnail("");
      setLink("");
      setPublished(true);
      setFeatured(false);
      setChallenge("");
      setApproach("");
      setOutcome("");
    }
  }, [selectedProject]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      title,
      description,
      thumbnail,
      link,
      published,
      featured,
      challenge,
      approach,
      outcome,
    });
  };

  const handleCancel = () => {
    setTitle("");
    setDescription("");
    setThumbnail("");
    setLink("");
    setPublished(true);
    setFeatured(false);
    setChallenge("");
    setApproach("");
    setOutcome("");
    onCancel();
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-lg border border-gray-200 dark:border-gray-700">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-brand-gold">
          {selectedProject ? "Edit Project" : "Create New Project"}
        </h2>
        {selectedProject && (
          <button
            onClick={handleCancel}
            className="p-2 text-gray-400 hover:text-red-400 transition-colors"
            title="Cancel editing"
          >
            <FiX size={20} />
          </button>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label
            htmlFor="project-title"
            className="block text-sm font-medium text-gray-300 mb-2"
          >
            Project Title *
          </label>
          <input
            id="project-title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-gold focus:border-transparent"
            placeholder="Enter project title..."
            required
          />
        </div>

        <div>
          <label
            htmlFor="project-description"
            className="block text-sm font-medium text-gray-300 mb-2"
          >
            Description *
          </label>
          <textarea
            id="project-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={4}
            className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-lime-500 focus:border-transparent resize-vertical"
            placeholder="Describe your project..."
            required
          />
        </div>

        <div>
          <label
            htmlFor="project-thumbnail"
            className="block text-sm font-medium text-gray-300 mb-2"
          >
            Thumbnail URL *
          </label>
          <input
            id="project-thumbnail"
            type="url"
            value={thumbnail}
            onChange={(e) => setThumbnail(e.target.value)}
            className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-gold focus:border-transparent"
            placeholder="https://example.com/image.jpg"
            required
          />
        </div>

        <div>
          <label
            htmlFor="project-link"
            className="block text-sm font-medium text-gray-300 mb-2"
          >
            Project Link *
          </label>
          <input
            id="project-link"
            type="url"
            value={link}
            onChange={(e) => setLink(e.target.value)}
            className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-gold focus:border-transparent"
            placeholder="https://github.com/username/project"
            required
          />
        </div>

        <div className="grid sm:grid-cols-3 gap-4">
          <div>
            <label
              htmlFor="project-challenge"
              className="block text-sm font-medium text-gray-300 mb-2"
            >
              Challenge
            </label>
            <textarea
              id="project-challenge"
              value={challenge}
              onChange={(e) => setChallenge(e.target.value)}
              rows={3}
              className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-gold focus:border-transparent resize-vertical"
              placeholder="What problem did this solve?"
            />
          </div>
          <div>
            <label
              htmlFor="project-approach"
              className="block text-sm font-medium text-gray-300 mb-2"
            >
              Approach
            </label>
            <textarea
              id="project-approach"
              value={approach}
              onChange={(e) => setApproach(e.target.value)}
              rows={3}
              className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-gold focus:border-transparent resize-vertical"
              placeholder="How did you build it?"
            />
          </div>
          <div>
            <label
              htmlFor="project-outcome"
              className="block text-sm font-medium text-gray-300 mb-2"
            >
              Outcome
            </label>
            <textarea
              id="project-outcome"
              value={outcome}
              onChange={(e) => setOutcome(e.target.value)}
              rows={3}
              className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-gold focus:border-transparent resize-vertical"
              placeholder="What was the result?"
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-6">
          <div className="flex items-center gap-3">
            <input
              id="project-published"
              type="checkbox"
              checked={published}
              onChange={(e) => setPublished(e.target.checked)}
              className="w-4 h-4 text-brand-gold bg-gray-700 border-gray-600 rounded focus:ring-brand-gold focus:ring-2"
            />
            <label
              htmlFor="project-published"
              className="text-sm font-medium text-gray-300"
            >
              Publish immediately
            </label>
          </div>
          <div className="flex items-center gap-3">
            <input
              id="project-featured"
              type="checkbox"
              checked={featured}
              onChange={(e) => setFeatured(e.target.checked)}
              className="w-4 h-4 text-brand-gold bg-gray-700 border-gray-600 rounded focus:ring-brand-gold focus:ring-2"
            />
            <label
              htmlFor="project-featured"
              className="text-sm font-medium text-gray-300"
            >
              Feature on Home page
            </label>
          </div>
        </div>

        <div className="flex gap-3 pt-4">
          <button
            type="submit"
            disabled={isLoading}
            className="flex items-center gap-2 bg-brand-gold hover:bg-brand-gold/90 disabled:bg-gray-600 text-black font-bold px-4 py-2 rounded-md transition-colors font-medium"
          >
            <FiSave size={16} />
            {isLoading
              ? "Saving..."
              : selectedProject
              ? "Update Project"
              : "Create Project"}
          </button>

          {selectedProject && (
            <button
              type="button"
              onClick={handleCancel}
              className="flex items-center gap-2 bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md transition-colors font-medium"
            >
              <FiX size={16} />
              Cancel
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default ProjectForm;
